import React, { Component } from "react";
import { connect } from "react-redux";
import { injectIntl, intlShape } from "@ai360/core/intl";
import classnames from "classnames";
import moment from "moment";

import { actions as accordionActions, model as accordionModel } from "~/accordion";
import { LayerAPI, LayerUtilsAPI, UserAPI } from "@ai360/core";
import { Loader, LoaderTypes, toTestIdSegment } from "~/core";
import { getUser } from "~/login";

import { createSurfaceAccordionItems } from "./surface-item";
import * as actions from "../actions";
import * as selectors from "../selectors";
import { isEventOrRecLayerType, LayerTypeShape, getLayerTypeLetter } from "../../../utils";
import { messages } from "../../../components/i18n-messages";

import {
    ANALYSIS_INFO_NAME_IMAGERY_SETUP_PLANET,
    ANALYSIS_INFO_NAME_IMAGERY_SETUP_SENTINEL,
    ANALYSIS_INFO_NAME_MANAGEMENT_AREA,
    ANALYSIS_INFO_NAME_PROFIT_LOSS,
} from "~/recs-events/analysis/model";
import "./layer-item.css";
import LayerItemContextMenu from "../../context-menus/layer-item-context-menu";

export const LAYER_HEIGHT = 25;
export const COMPLETED = "Completed";
export const ERROR = "Error";
export const IMAGERY_UNAVAILABLE = "Imagery Unavailable";
export const SATELLITE_API_ERROR = "Satellite API Error";
export const NEW_SETUP_PENDING = "New Setup Pending";
export const PLANET_PENDING = "Pending";
export const PROCESSING_ERROR = "Processing Error";
export const FAILED = "Failed";

export const createLayerAccordionItems = (layers: LayerAPI.ILayer[]) => {
    return layers.map((layer, index) => {
        const type = LayerUtilsAPI.getLayerType(layer);
        const payload = {
            agEventGeneralGuid: layer.agEventGeneralGuid,
            cropGuid: layer.cropGuid,
            croppingSeasonGuid: layer.croppingSeasonGuid,
            index,
            layerType: layer.layerType,
            type,
        };
        const layerItem = new accordionModel.AccordionItem(LAYER_HEIGHT, false, payload);
        const attributeGuidField = LayerUtilsAPI.getAttributeGuidFieldName(type);

        switch (type) {
            case LayerUtilsAPI.LayerType.EVENT_FROM_EQUATION_REC:
                return accordionModel.AccordionItem.updateAccordionItem(layerItem, {
                    children: createSurfaceAccordionItems(
                        layer.subLayers.filter((s) => {
                            return s.surfaceTypeGuid === layer.selectedSurfaceTypeGuid;
                        }),
                        type
                    ),
                });
            case LayerUtilsAPI.LayerType.EVENT_IMPORTED:
                if (layer.isSampling) {
                    if (layer.subLayers) {
                        return accordionModel.AccordionItem.updateAccordionItem(layerItem, {
                            children: createSurfaceAccordionItems(
                                [
                                    {
                                        classBreaks: [],
                                        surfaceGuid: LayerUtilsAPI.SAMPLE_SITES_GUID,
                                    },
                                    ...layer.subLayers.filter((s) => {
                                        return s.surfaceTypeGuid === layer.selectedSurfaceTypeGuid;
                                    }),
                                ],
                                type
                            ),
                        });
                    } else {
                        return accordionModel.AccordionItem.updateAccordionItem(layerItem, {
                            children: createSurfaceAccordionItems(
                                [
                                    {
                                        classBreaks: [],
                                        surfaceGuid: LayerUtilsAPI.SAMPLE_SITES_GUID,
                                    },
                                ],
                                type
                            ),
                        });
                    }
                } else {
                    return accordionModel.AccordionItem.updateAccordionItem(layerItem, {
                        children: createSurfaceAccordionItems(
                            layer.subLayers.filter((s) => {
                                return s.surfaceTypeGuid === layer.selectedSurfaceTypeGuid;
                            }),
                            type
                        ),
                    });
                }
            case LayerUtilsAPI.LayerType.EVENT_MANUAL:
                return accordionModel.AccordionItem.updateAccordionItem(layerItem, {
                    children: createSurfaceAccordionItems(
                        layer.subLayers.filter((s) => {
                            return s.surfaceTypeGuid === layer.selectedSurfaceTypeGuid;
                        }),
                        type
                    ),
                });
            case LayerUtilsAPI.LayerType.MANAGEMENT_AREA:
                return accordionModel.AccordionItem.updateAccordionItem(layerItem, {
                    children: createSurfaceAccordionItems(
                        layer.subLayers.filter((s) => {
                            return (
                                s.surfaceGuid === layer.selectedSurfaceGuid &&
                                s.surfaceTypeGuid === s.selectedSurfaceTypeGuid
                            );
                        }),
                        type
                    ),
                });
            case LayerUtilsAPI.LayerType.ANALYSIS:
            case LayerUtilsAPI.LayerType.REC:
                return accordionModel.AccordionItem.updateAccordionItem(layerItem, {
                    children: createSurfaceAccordionItems(
                        layer.subLayers.filter((s) => {
                            return s.surfaceTypeGuid === s.selectedSurfaceTypeGuid;
                        }),
                        type
                    ),
                });
            case LayerUtilsAPI.LayerType.SOIL:
                if (layer.subLayers) {
                    return accordionModel.AccordionItem.updateAccordionItem(layerItem, {
                        children: createSurfaceAccordionItems(
                            layer.subLayers.filter((s) => {
                                return s[attributeGuidField] === layer.selectedAttributeGuid;
                            }),
                            type
                        ),
                    });
                }
                return layerItem;
            case LayerUtilsAPI.LayerType.IMAGERY:
                return accordionModel.AccordionItem.updateAccordionItem(layerItem, {
                    children: createSurfaceAccordionItems(layer.subLayers, type),
                });
            default:
                return layerItem;
        }
    });
};

interface ILayerItemProps {
    accordionId: number;
    collapseItem: (accordionId: number, itemDimIdx: number[]) => void;
    displayNamePrefix: string;
    expandItem: (accordionId: number, itemDimIdx: number[]) => void;
    fieldGuid: string;
    hasVisibleSurface: boolean;
    index: number;
    intl: intlShape;
    isExpanded: boolean;
    itemDimIdx: number[];
    itemList: accordionModel.AccordionItem[];
    layer: LayerAPI.ILayer;
    setScrollToFlatIdx: (flatIdx: number | null) => void;
    status: string;
    type: typeof LayerTypeShape;
    userInfo: UserAPI.IUser;
}

class LayerItem_ extends Component<ILayerItemProps> {
    _onClick(event) {
        if (event.isDefaultPrevented()) {
            return;
        }

        const {
            isExpanded,
            expandItem,
            collapseItem,
            accordionId,
            itemDimIdx,
            itemList,
            setScrollToFlatIdx,
        } = this.props;
        if (isExpanded) {
            collapseItem(accordionId, itemDimIdx);
            setScrollToFlatIdx(null);
        } else {
            expandItem(accordionId, itemDimIdx);
            // scroll so the newly shown children aren't hidden below the fold
            setScrollToFlatIdx(accordionModel.getFlatIdxFromDimIdx(itemList, itemDimIdx));
        }
    }

    useCroppingSeasonCombo(layer) {
        return (
            (layer.layerType === ANALYSIS_INFO_NAME_PROFIT_LOSS ||
                layer.layerType === ANALYSIS_INFO_NAME_MANAGEMENT_AREA) &&
            layer.subLayers?.length > 0 &&
            layer.subLayers[0].croppingSeason !== ""
        );
    }

    getDisplayName() {
        const { displayNamePrefix, layer } = this.props;
        let { displayName } = layer;
        if (displayNamePrefix) {
            if (this.useCroppingSeasonCombo(layer)) {
                displayName = `${layer.layerType} - ${layer.subLayers[0].croppingSeason} - ${displayName}`;
            } else if (layer.layerType === ANALYSIS_INFO_NAME_MANAGEMENT_AREA) {
                displayName = `${layer.layerType} - ${displayName}`;
            } else {
                displayName = `${displayNamePrefix} - ${displayName}`;
            }
        }
        return displayName;
    }

    getTooltipText() {
        const { layer, userInfo } = this.props;
        const formatedDate = moment(layer.layerDate).format("M/D/YY - h:mm a");

        const user =
            layer.modifiedBy != null && layer.modifiedBy?.trim() !== ""
                ? `${layer.modifiedBy} - `
                : userInfo != null && userInfo.personProperties != null
                ? `${userInfo.personProperties.firstName} ${userInfo.personProperties.lastName} - `
                : "";
        const date = formatedDate ? formatedDate + " " : "";

        const toolTipText = `${user}${date}`;
        return toolTipText;
    }

    getToolTipTextImagerySetup() {
        const { status } = this.props;
        const { formatMessage } = this.props.intl;
        if (status === IMAGERY_UNAVAILABLE) {
            return formatMessage(messages.imagerySetupUnavailable);
        } else if (status === ERROR) {
            return formatMessage(messages.imagerySetupError);
        }
        return this.getTooltipText();
    }

    render() {
        const { fieldGuid, hasVisibleSurface, isExpanded, layer, status, type, userInfo } =
            this.props;
        const { formatMessage } = this.props.intl;

        if (!layer) {
            return null;
        }

        const displayName = this.getDisplayName();
        const statusClass =
            status === COMPLETED || status === NEW_SETUP_PENDING || status === PLANET_PENDING
                ? "layer-status-completed"
                : status === PROCESSING_ERROR || status === FAILED
                ? "layer-status-error"
                : "layer-status";
        const isImagerySetupLayer =
            layer.layerType === ANALYSIS_INFO_NAME_IMAGERY_SETUP_PLANET ||
            layer.layerType === ANALYSIS_INFO_NAME_IMAGERY_SETUP_SENTINEL;
        const toolTip = isImagerySetupLayer
            ? this.getToolTipTextImagerySetup()
            : this.getTooltipText();
        const isImagerySetuploading =
            isImagerySetupLayer &&
            status !== COMPLETED &&
            status !== NEW_SETUP_PENDING &&
            status !== PLANET_PENDING &&
            status !== ERROR &&
            status !== SATELLITE_API_ERROR &&
            status !== IMAGERY_UNAVAILABLE;
        const isFailedManagementArea =
            layer.isManagementArea && layer.analysisLayerStatus === "Failed";
        const displayLayerItemContextMenu =
            isFailedManagementArea ||
            isEventOrRecLayerType(LayerUtilsAPI.getLayerType(layer)) ||
            !(!isImagerySetupLayer || isImagerySetuploading || !userInfo.role.imagerySetup);
        const onClick = isFailedManagementArea
            ? (e) => e.preventDefault()
            : (event) => this._onClick(event);
        const loader = !isImagerySetuploading ? null : (
            <div className="layer-item-loader">
                <Loader className="layer-loader" type={LoaderTypes.BALL_CLIP_ROTATE} />
            </div>
        );

        return (
            <div
                className={classnames("layer-accordion-item", { expanded: isExpanded })}
                onClick={onClick}
                data-testid={`layer-accordion-item-${toTestIdSegment(displayName)}`}
            >
                <div
                    className={classnames("letter-icon", {
                        "has-visible": hasVisibleSurface,
                    })}
                >
                    {getLayerTypeLetter(type, formatMessage)}
                </div>
                <div className="layer-label" title={displayName}>
                    {displayName}
                </div>
                {!status ? null : (
                    <div title={toolTip} className={statusClass}>
                        {loader}
                        {status}
                    </div>
                )}
                {displayLayerItemContextMenu && (
                    <LayerItemContextMenu layer={layer} type={Number(type)} fieldGuid={fieldGuid} />
                )}
            </div>
        );
    }
}

const mapDispatchToProps = (dispatch) => ({
    expandItem: (accordionId, dimIdx) =>
        dispatch(accordionActions.expandAccordionItem(accordionId, dimIdx)),
    collapseItem: (accordionId, dimIdx) =>
        dispatch(accordionActions.collapseAccordionItem(accordionId, dimIdx)),
    setScrollToFlatIdx: (flatIdx) => dispatch(actions.setScrollToFlatIdx(flatIdx)),
});

const mapStateToProps = (state, ownProps) => {
    const layerInfos = selectors.getLayerInfos(state).get(ownProps.fieldGuid);
    const layer = layerInfos ? layerInfos[ownProps.index] : null;
    const userInfo = getUser(state);
    const itemList = selectors.getAccordionItems(state);
    //For some reason, calling remove layer infos doesn't stop this from loading - may need to track this down
    if (!layerInfos || !layer) {
        return {
            displayNamePrefix: "",
            itemList,
            layer: null,
            status: "",
        };
    }
    let { displayNamePrefix, status } = layer;
    if (!status) {
        status = null;
    }
    if (ownProps.type === LayerUtilsAPI.LayerType.MANAGEMENT_AREA) {
        displayNamePrefix = layer.layerType;
        status = layer.analysisLayerStatus;
    } else if (
        layer.layerType === ANALYSIS_INFO_NAME_IMAGERY_SETUP_PLANET ||
        layer.layerType === ANALYSIS_INFO_NAME_IMAGERY_SETUP_SENTINEL
    ) {
        displayNamePrefix = "Imagery";
        status = status || layer.analysisLayerStatus;
    } else if (
        ownProps.type === LayerUtilsAPI.LayerType.ANALYSIS &&
        layer.subLayers.length > 0 &&
        layer.type !== ANALYSIS_INFO_NAME_IMAGERY_SETUP_PLANET &&
        layer.type !== ANALYSIS_INFO_NAME_IMAGERY_SETUP_SENTINEL
    ) {
        displayNamePrefix =
            layer.layerType === ANALYSIS_INFO_NAME_PROFIT_LOSS
                ? (displayNamePrefix = layer.layerType)
                : LayerUtilsAPI.getSurfaceInfo(layer).displayName;
        status = layer.analysisLayerStatus;
    }
    return {
        displayNamePrefix,
        itemList,
        layer,
        status,
        userInfo,
    };
};

export const LayerItem = connect(mapStateToProps, mapDispatchToProps)(injectIntl(LayerItem_));
